/**
 * Geometry helpers for the simple Property Card builder
 * (side lengths, area, centroid, unit systems).
 */
import area from '@turf/area';
import length from '@turf/length';
import centroid from '@turf/centroid';
import { lineString, polygon } from '@turf/helpers';

export type LonLat = [number, number]; // [lon, lat]

export type LengthUnit = 'm' | 'ft' | 'yd';
export type AreaUnit = 'auto' | 'm2' | 'sqft' | 'acre' | 'hectare';

export interface SideMeasure {
  index: number;
  /** metres (canonical) */
  metres: number;
  label: string;
  mid: LonLat;
}

export interface ParcelMetrics {
  sides: SideMeasure[];
  areaM2: number;
  areaAcres: number;
  areaSqFt: number;
  areaHectares: number;
  areaLabel: string;
  perimeterM: number;
  perimeterLabel: string;
  centroid: LonLat;
  ring: LonLat[];
}

export function formatLength(m: number, unit: LengthUnit = 'm'): string {
  if (unit === 'ft') {
    const ft = m * 3.280839895;
    return ft >= 1000 ? `${(ft / 5280).toFixed(2)} mi` : `${ft.toFixed(1)} ft`;
  }
  if (unit === 'yd') {
    const yd = m * 1.0936132983;
    return `${yd.toFixed(1)} yd`;
  }
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  if (m >= 100) return `${m.toFixed(1)} m`;
  return `${m.toFixed(2)} m`;
}

export function formatArea(
  m2: number,
  unit: AreaUnit = 'auto'
): {
  acres: number;
  sqFt: number;
  hectares: number;
  label: string;
} {
  const acres = m2 / 4046.8564224;
  const sqFt = m2 * 10.76391041671;
  const hectares = m2 / 10000;
  let label: string;
  switch (unit) {
    case 'm2':
      label = `${m2.toFixed(m2 >= 100 ? 1 : 2)} m²`;
      break;
    case 'sqft':
      label = `${sqFt.toLocaleString('en-IN', { maximumFractionDigits: 0 })} sq ft`;
      break;
    case 'acre':
      label = `${acres.toFixed(3)} ac`;
      break;
    case 'hectare':
      label = `${hectares.toFixed(3)} ha`;
      break;
    default:
      if (acres >= 0.1) {
        label = `${acres.toFixed(2)} ac · ${sqFt.toLocaleString('en-IN', { maximumFractionDigits: 0 })} sq ft`;
      } else if (m2 >= 1) {
        label = `${m2.toFixed(1)} m² · ${sqFt.toFixed(0)} sq ft`;
      } else {
        label = `${m2.toFixed(2)} m²`;
      }
  }
  return { acres, sqFt, hectares, label };
}

/**
 * Build metrics for a closed parcel from corner points (lon/lat).
 */
export function computeParcelMetrics(
  corners: LonLat[],
  opts?: { lengthUnit?: LengthUnit; areaUnit?: AreaUnit }
): ParcelMetrics | null {
  if (corners.length < 3) return null;

  const lengthUnit = opts?.lengthUnit ?? 'm';
  const areaUnit = opts?.areaUnit ?? 'auto';

  const ring: LonLat[] = [...corners];
  const first = corners[0];
  const last = corners[corners.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]]);
  }
  if (ring.length < 4) return null;

  const sides: SideMeasure[] = [];
  let perimeterM = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i];
    const b = ring[i + 1];
    const metres = length(lineString([a, b]), { units: 'meters' });
    perimeterM += metres;
    sides.push({
      index: i + 1,
      metres,
      label: `S${i + 1}: ${formatLength(metres, lengthUnit)}`,
      mid: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
    });
  }

  let areaM2 = 0;
  try {
    areaM2 = Math.abs(area(polygon([ring])));
  } catch {
    return null;
  }

  const { acres, sqFt, hectares, label: areaLabel } = formatArea(areaM2, areaUnit);

  let c: LonLat = ring[0];
  try {
    c = centroid(polygon([ring])).geometry.coordinates as LonLat;
  } catch {
    /* keep first */
  }

  return {
    sides,
    areaM2,
    areaAcres: acres,
    areaSqFt: sqFt,
    areaHectares: hectares,
    areaLabel,
    perimeterM,
    perimeterLabel: `Perimeter: ${formatLength(perimeterM, lengthUnit)}`,
    centroid: c,
    ring,
  };
}

export function formatCoord(lon: number, lat: number): string {
  return `${lat.toFixed(5)}° N, ${lon.toFixed(5)}° E`;
}

/** Viral share body for WhatsApp / Telegram / etc. */
export function buildViralShareText(opts: {
  name: string;
  digipin?: string | null;
  areaLabel?: string;
  address?: string | null;
  perimeterLabel?: string;
  shareUrl: string;
  owner?: string;
}): string {
  const lines = [`📍 *GeoVera Digital Property Card*`, ``, `*${opts.name}*`];
  if (opts.owner) lines.push(`Owner: ${opts.owner}`);
  if (opts.digipin) lines.push(`DIGIPIN: ${opts.digipin}`);
  if (opts.areaLabel) lines.push(`Area: ${opts.areaLabel}`);
  if (opts.perimeterLabel) lines.push(opts.perimeterLabel);
  if (opts.address) lines.push(`Address: ${opts.address}`);
  lines.push(``, `View free map:`, opts.shareUrl, ``, `— Made free on GeoVera WebGIS · geovera.in`);
  return lines.join('\n');
}
